/**
 * Position-line geometry — bearings, transits, ranges, three-point fix,
 * running fix.
 *
 * For RYA-scale exercises (single training cells, max a few tens of nm) we
 * treat the chart as a local plane in equirectangular projection around the
 * working point. The errors versus full geodesic maths are well below the
 * width of a 0.3 mm pencil line, and the algebra is straightforward enough to
 * unit-test against worked paper-chart examples.
 *
 * If we ever extend this to ocean-scale work, the projection swap is the only
 * thing that changes: every entry point here takes [lon, lat] and returns
 * [lon, lat], and the local-plane projector is private to this file.
 */
import { normaliseBearing, rhumbBearingDeg, rhumbDistanceNm, rhumbDestination } from './geodesy';

const D2R = Math.PI / 180;
const NM_PER_DEG_LAT = 60;

/** Local-plane projection around an anchor point. Returns metres-equivalent in nm. */
function project([lon, lat]: [number, number], [lon0, lat0]: [number, number]): [number, number] {
  const x = (lon - lon0) * NM_PER_DEG_LAT * Math.cos(lat0 * D2R);
  const y = (lat - lat0) * NM_PER_DEG_LAT;
  return [x, y];
}
function unproject([x, y]: [number, number], [lon0, lat0]: [number, number]): [number, number] {
  const lon = lon0 + x / (NM_PER_DEG_LAT * Math.cos(lat0 * D2R));
  const lat = lat0 + y / NM_PER_DEG_LAT;
  return [lon, lat];
}

// -----------------------------------------------------------------------------
// Position lines
// -----------------------------------------------------------------------------

export interface BearingLine {
  type: 'bearing';
  /** The charted feature we took the bearing on. */
  from: [number, number];
  /** Bearing taken (degrees true), looking at the feature. */
  bearingDeg: number;
}

export interface RangeCircle {
  type: 'range';
  centre: [number, number];
  rangeNm: number;
}

export type PositionLine = BearingLine | RangeCircle;

/**
 * Intersect two position lines. Returns 0, 1 or 2 candidate positions.
 *
 * For two bearings: one intersection (or zero if parallel).
 * For two ranges: zero, one or two intersections.
 * For a bearing + range: zero, one or two.
 */
export function intersect(a: PositionLine, b: PositionLine): [number, number][] {
  // Pick an anchor for the local plane — midpoint of the input "centres".
  const anchorLon =
    (positionLineAnchor(a)[0] + positionLineAnchor(b)[0]) / 2;
  const anchorLat =
    (positionLineAnchor(a)[1] + positionLineAnchor(b)[1]) / 2;
  const anchor: [number, number] = [anchorLon, anchorLat];

  if (a.type === 'bearing' && b.type === 'bearing') {
    return intersectBearingBearing(a, b, anchor);
  }
  if (a.type === 'range' && b.type === 'range') {
    return intersectRangeRange(a, b, anchor);
  }
  if (a.type === 'bearing' && b.type === 'range') return intersectBearingRange(a, b, anchor);
  return intersectBearingRange(b as BearingLine, a as RangeCircle, anchor);
}

function positionLineAnchor(pl: PositionLine): [number, number] {
  return pl.type === 'bearing' ? pl.from : pl.centre;
}

function intersectBearingBearing(
  a: BearingLine,
  b: BearingLine,
  anchor: [number, number],
): [number, number][] {
  const [ax, ay] = project(a.from, anchor);
  const [bx, by] = project(b.from, anchor);
  // Direction vectors for each line (degrees true: 0 = +Y axis, 90 = +X).
  const ad = bearingToDir(a.bearingDeg);
  const bd = bearingToDir(b.bearingDeg);
  // Solve: a + t * ad = b + u * bd.
  const det = ad[0] * (-bd[1]) - ad[1] * (-bd[0]);
  if (Math.abs(det) < 1e-9) return [];
  const dx = bx - ax;
  const dy = by - ay;
  const t = (dx * -bd[1] - dy * -bd[0]) / det;
  const ix = ax + t * ad[0];
  const iy = ay + t * ad[1];
  return [unproject([ix, iy], anchor)];
}

function intersectRangeRange(
  a: RangeCircle,
  b: RangeCircle,
  anchor: [number, number],
): [number, number][] {
  const [ax, ay] = project(a.centre, anchor);
  const [bx, by] = project(b.centre, anchor);
  const dx = bx - ax;
  const dy = by - ay;
  const d = Math.hypot(dx, dy);
  if (d === 0) return [];
  if (d > a.rangeNm + b.rangeNm) return [];
  if (d < Math.abs(a.rangeNm - b.rangeNm)) return [];
  const aHalf = (a.rangeNm * a.rangeNm - b.rangeNm * b.rangeNm + d * d) / (2 * d);
  const h2 = a.rangeNm * a.rangeNm - aHalf * aHalf;
  const h = h2 > 0 ? Math.sqrt(h2) : 0;
  const mx = ax + (aHalf * dx) / d;
  const my = ay + (aHalf * dy) / d;
  const rx = (h * -dy) / d;
  const ry = (h * dx) / d;
  if (h === 0) return [unproject([mx, my], anchor)];
  return [unproject([mx + rx, my + ry], anchor), unproject([mx - rx, my - ry], anchor)];
}

function intersectBearingRange(
  bear: BearingLine,
  rng: RangeCircle,
  anchor: [number, number],
): [number, number][] {
  const [bx, by] = project(bear.from, anchor);
  const [cx, cy] = project(rng.centre, anchor);
  const [dx, dy] = bearingToDir(bear.bearingDeg);
  // Line: (bx + t dx, by + t dy). Circle: (x - cx)^2 + (y - cy)^2 = r^2.
  const fx = bx - cx;
  const fy = by - cy;
  const A = dx * dx + dy * dy;
  const B = 2 * (fx * dx + fy * dy);
  const C = fx * fx + fy * fy - rng.rangeNm * rng.rangeNm;
  const disc = B * B - 4 * A * C;
  if (disc < 0) return [];
  const sq = Math.sqrt(disc);
  const t1 = (-B - sq) / (2 * A);
  const t2 = (-B + sq) / (2 * A);
  const pts: [number, number][] = [];
  for (const t of disc === 0 ? [t1] : [t1, t2]) {
    pts.push(unproject([bx + t * dx, by + t * dy], anchor));
  }
  return pts;
}

function bearingToDir(degrees: number): [number, number] {
  const rad = degrees * D2R;
  // x = sin(theta), y = cos(theta) so 0° -> (0, +1), 90° -> (+1, 0).
  return [Math.sin(rad), Math.cos(rad)];
}

// -----------------------------------------------------------------------------
// Three-point fix — the most likely position from N position lines.
// -----------------------------------------------------------------------------

export interface FixResult {
  /** Best-estimate position [lon, lat]. */
  position: [number, number];
  /** The triangle / polygon of error vertices in [lon, lat]. */
  cocked: [number, number][];
  /** Largest inter-intersection separation in nm (cocked-hat size). */
  uncertaintyNm: number;
}

/**
 * Compute a fix from at least two position lines.
 *
 * If three or more lines are supplied, every pair is intersected. The pair-wise
 * intersections form the "cocked hat" of error. The reported position is the
 * centroid of that polygon. The uncertainty is the diameter of the polygon.
 *
 * For range-circle pairs that produce two candidates, the closer of the two
 * to the centroid of the other lines is taken. This matches what a student
 * does on paper.
 */
export function fixFromLines(lines: PositionLine[]): FixResult | null {
  if (lines.length < 2) return null;

  // Anchor for the local plane: average of all line anchors.
  const anchorLon =
    lines.reduce((s, l) => s + positionLineAnchor(l)[0], 0) / lines.length;
  const anchorLat =
    lines.reduce((s, l) => s + positionLineAnchor(l)[1], 0) / lines.length;
  const anchor: [number, number] = [anchorLon, anchorLat];

  const projectedCenter: [number, number] = [0, 0]; // anchor sits at the origin

  const intersections: [number, number][] = [];
  for (let i = 0; i < lines.length; i++) {
    for (let j = i + 1; j < lines.length; j++) {
      const pts = intersect(lines[i], lines[j]);
      if (pts.length === 0) continue;
      // Pick the candidate closest to the anchor — matches the paper convention.
      const proj = pts.map((p) => project(p, anchor));
      let best = proj[0];
      let bestD = dist2(best, projectedCenter);
      for (let k = 1; k < proj.length; k++) {
        const d = dist2(proj[k], projectedCenter);
        if (d < bestD) {
          best = proj[k];
          bestD = d;
        }
      }
      intersections.push(best);
    }
  }
  if (!intersections.length) return null;

  // Centroid in the local plane.
  const cx = intersections.reduce((s, p) => s + p[0], 0) / intersections.length;
  const cy = intersections.reduce((s, p) => s + p[1], 0) / intersections.length;

  // Cocked-hat size: max pair distance.
  let maxNm = 0;
  for (let i = 0; i < intersections.length; i++) {
    for (let j = i + 1; j < intersections.length; j++) {
      const dx = intersections[i][0] - intersections[j][0];
      const dy = intersections[i][1] - intersections[j][1];
      const d = Math.hypot(dx, dy);
      if (d > maxNm) maxNm = d;
    }
  }

  return {
    position: unproject([cx, cy], anchor),
    cocked: intersections.map((p) => unproject(p, anchor)),
    uncertaintyNm: maxNm,
  };
}

function dist2(a: [number, number], b: [number, number]): number {
  const dx = a[0] - b[0];
  const dy = a[1] - b[1];
  return dx * dx + dy * dy;
}

// -----------------------------------------------------------------------------
// Running fix — transferred position line.
// -----------------------------------------------------------------------------

/**
 * Transfer an earlier position line forward by the boat's run (course + speed
 * × time interval) so it can be combined with a later position line into a
 * "running fix".
 *
 * On paper: take the original bearing, translate it parallel by the run vector,
 * and intersect the translated line with the new bearing.
 *
 * For a bearing line, the transferred line has the same bearing; its anchor
 * point moves by the run. For a range circle, the centre moves by the same.
 */
export interface Run {
  /** Course over ground in degrees true. */
  cogDeg: number;
  /** Speed over ground in knots. */
  sogKnots: number;
  /** Time interval since the original position line, in hours. */
  hours: number;
}

export function transferLine(line: PositionLine, run: Run): PositionLine {
  const runNm = run.sogKnots * run.hours;
  if (line.type === 'bearing') {
    return {
      type: 'bearing',
      from: rhumbDestination(line.from, run.cogDeg, runNm),
      bearingDeg: line.bearingDeg,
    };
  }
  return {
    type: 'range',
    centre: rhumbDestination(line.centre, run.cogDeg, runNm),
    rangeNm: line.rangeNm,
  };
}

/**
 * Running fix from a single earlier bearing line and a later bearing line on
 * the same charted feature (or different features — the maths is the same):
 * transfer the earlier line by the run, then intersect.
 */
export function runningFix(
  earlier: PositionLine,
  run: Run,
  later: PositionLine,
): { transferred: PositionLine; fix: [number, number] | null } {
  const transferred = transferLine(earlier, run);
  const pts = intersect(transferred, later);
  return { transferred, fix: pts[0] ?? null };
}

// -----------------------------------------------------------------------------
// Distance to run (XTE-aware) — useful for both routing and chartwork.
// -----------------------------------------------------------------------------

export interface DtrResult {
  /** Cross-track distance from the present position, perpendicular, in nm.
   *  Positive = right of track, negative = left. */
  xteNm: number;
  /** Remaining distance from foot of perpendicular to leg end, in nm. */
  distanceToGoNm: number;
}

export function distanceToRun(
  position: [number, number],
  legStart: [number, number],
  legEnd: [number, number],
): DtrResult {
  const anchor: [number, number] = [
    (legStart[0] + legEnd[0]) / 2,
    (legStart[1] + legEnd[1]) / 2,
  ];
  const [px, py] = project(position, anchor);
  const [sx, sy] = project(legStart, anchor);
  const [ex, ey] = project(legEnd, anchor);
  const lx = ex - sx;
  const ly = ey - sy;
  const legLen = Math.hypot(lx, ly);
  if (legLen === 0) {
    return { xteNm: rhumbDistanceNm(position, legStart), distanceToGoNm: 0 };
  }
  // Component along leg direction.
  const along = ((px - sx) * lx + (py - sy) * ly) / legLen;
  // Cross-track component (signed: positive = right of track).
  const cross = ((px - sx) * (-ly) + (py - sy) * lx) / legLen;
  // Need to flip the sign: in our (x east, y north) plane, the "right of track"
  // when looking from start to end is the rotation of the leg vector by -90°,
  // which is (ly, -lx). Recompute properly:
  const rightX = ly;
  const rightY = -lx;
  const xte = ((px - sx) * rightX + (py - sy) * rightY) / legLen;
  void cross; // kept for clarity above; xte is the correct signed value
  return { xteNm: xte, distanceToGoNm: legLen - along };
}

// -----------------------------------------------------------------------------
// Reciprocal — handy for bearing-from-feature vs bearing-of-feature.
// -----------------------------------------------------------------------------

export function reciprocal(degrees: number): number {
  return normaliseBearing(degrees + 180);
}

/** Convenience: bearing FROM a → b, expressed as the bearing that would be
 *  read off a hand-bearing compass looking from b BACK to a. */
export function reverseBearing(from: [number, number], to: [number, number]): number {
  return reciprocal(rhumbBearingDeg(from, to));
}
